/**
 * /admin/login — email + password form.
 *
 * On success: bounces to the path that originally sent us here
 * (`location.state.from`) or `/` if there isn't one. On 401 the
 * backend returns `{ error: 'invalid_credentials' }` and we show a
 * generic "wrong email or password" line — never disambiguate which
 * one was wrong, since that's the email-enumeration leak the
 * timing-pad in `/auth/login` is also defending against.
 *
 * OCP-755 - an account with a second factor gets `mfa_required` after a
 * correct password: the form switches to the code step (authenticator code,
 * or one backup code) and resubmits both.
 */
import { useEffect, useState } from 'react';
import type { FormEvent } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';

import { ApiError, useAuth } from '../lib/auth-context';
import { OrbotoLockup } from '../components/OrbotoLockup';

interface LocationState {
  from?: string;
}

export function LoginPage() {
  const { status, login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const from = (location.state as LocationState | null)?.from ?? '/';

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [step, setStep] = useState<'password' | 'factor'>('password');
  const [useBackup, setUseBackup] = useState(false);
  const [factor, setFactor] = useState('');

  // If the operator hits /login while already authenticated, send them
  // home — keeps the back-button + bookmarked-login-url cases sane.
  useEffect(() => {
    if (status === 'authenticated') {
      navigate(from, { replace: true });
    }
  }, [status, from, navigate]);

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (submitting) return;
    setError(null);
    setSubmitting(true);
    try {
      await login(email, password, step === 'factor' ? (useBackup ? { backup_code: factor.trim() } : { totp_code: factor.trim() }) : undefined);
      navigate(from, { replace: true });
    } catch (err) {
      const code = err instanceof ApiError ? (err.body as { error?: string } | null)?.error : undefined;
      if (err instanceof ApiError && err.status === 401 && code === 'mfa_required') {
        setStep('factor');
      } else if (err instanceof ApiError && err.status === 401 && code === 'invalid_mfa_code') {
        setError(useBackup ? 'That backup code is not valid or was used already.' : 'Wrong code. Check the time on your device and try the current one.');
        setFactor('');
      } else if (err instanceof ApiError && err.status === 401) {
        setStep('password');
        setError('Wrong email or password.');
      } else if (err instanceof ApiError && err.status === 429) {
        // OCP-775: the per-IP bucket (1 minute) or the per-account budget
        // (15 minutes) - the api names the wait either way.
        const wait = (err.body as { retry_after_seconds?: number } | null)?.retry_after_seconds;
        setError(
          typeof wait === 'number' && wait > 0
            ? `Too many attempts. Try again in ${wait >= 90 ? `${Math.ceil(wait / 60)} minutes` : `${wait} seconds`}.`
            : 'Too many attempts. Try again in a minute.',
        );
      } else {
        setError('Could not sign in. Please try again.');
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-gray-50 dark:bg-gray-800 px-4 text-gray-900 dark:text-gray-100">
      <div className="w-full max-w-sm rounded-lg border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-8 shadow-sm">
        <div className="mb-6 flex flex-col items-center gap-2">
          <OrbotoLockup className="h-7 w-auto" />
          <div className="text-center text-xs text-gray-500">Control Plane — Operator sign-in</div>
        </div>

        <form onSubmit={onSubmit} className="flex flex-col gap-4" noValidate>
          {/* OCP-940 - the code step shows the code field ONLY: with the e-mail and password still in the
              form, a password manager (Bitwarden) pairs the username with the code field and fills the
              e-mail into it. The credentials stay in state for the second submit. */}
          {step === 'password' && (<>
          <label className="flex flex-col gap-1 text-sm">
            <span className="text-gray-700 dark:text-gray-300">Email</span>
            <input
              type="email"
              name="email"
              autoComplete="username"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="rounded border border-gray-300 dark:border-gray-700 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none"
            />
          </label>
          <label className="flex flex-col gap-1 text-sm">
            <span className="text-gray-700 dark:text-gray-300">Password</span>
            <input
              type="password"
              name="password"
              autoComplete="current-password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="rounded border border-gray-300 dark:border-gray-700 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none"
            />
          </label>
          </>)}

          {step === 'factor' && (
            <div className="flex items-center justify-between text-xs text-gray-500" data-testid="login-factor-account">
              <span>Signing in as {email}</span>
              <button type="button" onClick={() => { setStep('password'); setFactor(''); setUseBackup(false); setError(null); }} className="hover:text-brand-500">
                Back
              </button>
            </div>
          )}

          {step === 'factor' && (
            <label className="flex flex-col gap-1 text-sm">
              <span className="text-gray-700 dark:text-gray-300">{useBackup ? 'Backup code' : 'Authenticator code'}</span>
              <input
                type="text"
                id={useBackup ? 'backup_code' : 'totp'}
                name={useBackup ? 'backup_code' : 'totp'}
                autoComplete="one-time-code"
                inputMode={useBackup ? 'text' : 'numeric'}
                pattern={useBackup ? undefined : '[0-9]*'}
                maxLength={useBackup ? 32 : 8}
                autoFocus
                required
                value={factor}
                onChange={(e) => setFactor(e.target.value)}
                placeholder={useBackup ? 'e.g. K7PQ2MXW9D' : '6 digits'}
                className="rounded border border-gray-300 dark:border-gray-700 px-3 py-2 text-sm tracking-widest focus:border-brand-500 focus:outline-none"
                data-testid="login-factor"
              />
              <button type="button" onClick={() => { setUseBackup((b) => !b); setFactor(''); setError(null); }} className="self-start text-xs text-gray-500 hover:text-brand-500">
                {useBackup ? 'Use the authenticator code' : 'Use a backup code instead'}
              </button>
            </label>
          )}

          {error && (
            <div role="alert" className="rounded border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/30 px-3 py-2 text-sm text-red-700 dark:text-red-300">
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={submitting}
            className="rounded bg-brand-500 px-3 py-2 text-sm font-medium text-white hover:bg-brand-600 active:bg-brand-700 disabled:cursor-not-allowed disabled:bg-gray-400"
          >
            {submitting ? 'Signing in…' : step === 'factor' ? 'Verify' : 'Sign in'}
          </button>

          <Link
            to="/forgot-password"
            className="text-center text-sm text-gray-500 hover:text-brand-500"
          >
            Forgot password?
          </Link>
        </form>
      </div>
    </div>
  );
}
